import { InputMode, InputType, MagicEmit, MuggleMixin, MagicConfigMixin } from '@gig/magic-common';
import { __extends, __decorate, __metadata } from 'tslib';
import { Prop, Watch, Emit, Component, Mixins } from 'vue-property-decorator';
import InputComponent from '../input/input.vue.js';
import '../input/index.js';

var isAndroid = /Android/.test(navigator.appVersion);
/**
 * Form field with a customizable slot that defaults to an input. It
 * has capabilities to show an error/warning message and an info
 * message. Use hasError, hasWarning and hasInfo to control their
 * display.
 */
var FormFieldComponent = /** @class */ (function (_super) {
    __extends(FormFieldComponent, _super);
    function FormFieldComponent() {
        var _this = _super !== null && _super.apply(this, arguments) || this;
        _this.focused = false;
        /**
         * Indicates if a problem (error or warning) was present when
         * the input lost focus last time. It is only updated to true
         * on blur, but it's updated to false immediately.
         */
        _this.hadProblem = false;
        _this.numeric = false;
        return _this;
    }
    Object.defineProperty(FormFieldComponent.prototype, "hasMessage", {
        get: function () {
            return !!this.message;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(FormFieldComponent.prototype, "hasProblem", {
        get: function () {
            return this.hasError || this.hasWarning || this.forceError;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(FormFieldComponent.prototype, "shouldDisplayMessage", {
        get: function () {
            return (((this.hasProblem &&
                (this.hadProblem ||
                    (!this.focused && Boolean(this.$attrs.touched)))) ||
                this.forceError) &&
                this.$attrs.disabled === undefined);
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(FormFieldComponent.prototype, "hasValidationState", {
        get: function () {
            return this.hasSuccess || this.hasError || this.hasWarning;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(FormFieldComponent.prototype, "attributes", {
        get: function () {
            return this.patchInputMode(this.$filteredAttrs);
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(FormFieldComponent.prototype, "isFakeNumericPassword", {
        get: function () {
            return (this.$filteredAttrs.inputmode === InputMode.NUMERIC &&
                this.$filteredAttrs.type === InputType.PASSWORD);
        },
        enumerable: false,
        configurable: true
    });
    FormFieldComponent.prototype.mounted = function () {
        var _this = this;
        var customElement = this.$root.$options.customElement;
        if (!customElement) {
            return;
        }
        customElement.addEventListener('focus', function () {
            var input = _this.$refs.input;
            if (!input) {
                return;
            }
            input.focus();
        });
    };
    FormFieldComponent.prototype.onBlur = function () {
        this.hadProblem = this.hasProblem;
        this.focused = false;
        this.$emit('on-focus', false);
    };
    FormFieldComponent.prototype.onHasProblemChanged = function (val) {
        if (!val) {
            this.hadProblem = false;
        }
        else {
            this.hadProblem = true;
        }
    };
    FormFieldComponent.prototype.onFocus = function (_a) {
        var target = _a.target;
        this.$emit('on-focus', true);
        this.focused = true;
        var focused = (target.shadowRoot && target.shadowRoot.activeElement) ||
            document.activeElement;
        if (!this.disableAutoscroll && isAndroid && focused) {
            // Android devices do not always correctly scroll to element
            // this handler essentially ensures that the input always shows
            // centered on page.
            setTimeout(function () {
                var _a = focused.getBoundingClientRect(), height = _a.height, top = _a.top;
                var centerY = window.scrollY + top + height / 2;
                window.scrollTo(0, centerY - window.innerHeight / 2);
            }, 200);
        }
    };
    /**
     * Re-emitted `input` event of the input inside form field.
     * Provides current input value
     */
    FormFieldComponent.prototype.onInput = function (event) {
        return this.getCustomEventValue(event);
    };
    /**
     * Re-emitted `change` event of the input inside form field.
     * Provides current input value
     */
    FormFieldComponent.prototype.onChange = function (event) {
        return this.getCustomEventValue(event);
    };
    FormFieldComponent.prototype.getCustomEventValue = function (event) {
        var e = event;
        if (e.detail && e.detail[0]) {
            return e.detail[0];
        }
        var target = event.target;
        if (target && target.value) {
            return target.value;
        }
        /**
         * if this wraps a select
         * none of the above will return
         */
        return null;
    };
    /**
     * Replaces inputs with type password and inputmode numeric
     * to type number
     *
     * This is to be removed once all mobile browsers will
     * support inputmode='numeric'
     * @param attr
     */
    FormFieldComponent.prototype.patchInputMode = function (attr) {
        if (attr.inputmode === InputMode.NUMERIC &&
            attr.type === InputType.PASSWORD) {
            attr.type = InputType.NUMBER;
        }
        return attr;
    };
    /**
     * Emitted when the user clicks the suggested email
     */
    FormFieldComponent.prototype.applySuggestion = function () {
        return this.suggestion;
    };
    __decorate([
        Prop(Boolean),
        __metadata("design:type", Boolean)
    ], FormFieldComponent.prototype, "hasError", void 0);
    __decorate([
        Prop(Boolean),
        __metadata("design:type", Boolean)
    ], FormFieldComponent.prototype, "hasSuccess", void 0);
    __decorate([
        Prop(Boolean),
        __metadata("design:type", Boolean)
    ], FormFieldComponent.prototype, "hasWarning", void 0);
    __decorate([
        Prop(Boolean),
        __metadata("design:type", Boolean)
    ], FormFieldComponent.prototype, "hasInfo", void 0);
    __decorate([
        Prop(Boolean),
        __metadata("design:type", Boolean)
    ], FormFieldComponent.prototype, "forceError", void 0);
    __decorate([
        Prop(String),
        __metadata("design:type", String)
    ], FormFieldComponent.prototype, "message", void 0);
    __decorate([
        Prop(String),
        __metadata("design:type", String)
    ], FormFieldComponent.prototype, "info", void 0);
    __decorate([
        Prop(String),
        __metadata("design:type", String)
    ], FormFieldComponent.prototype, "label", void 0);
    __decorate([
        Prop(Boolean),
        __metadata("design:type", Boolean)
    ], FormFieldComponent.prototype, "noMargin", void 0);
    __decorate([
        Prop(Boolean),
        __metadata("design:type", Boolean)
    ], FormFieldComponent.prototype, "centered", void 0);
    __decorate([
        Prop(Boolean),
        __metadata("design:type", Boolean)
    ], FormFieldComponent.prototype, "consent", void 0);
    __decorate([
        Prop(Boolean),
        __metadata("design:type", Boolean)
    ], FormFieldComponent.prototype, "noIcon", void 0);
    __decorate([
        Prop(Boolean),
        __metadata("design:type", Boolean)
    ], FormFieldComponent.prototype, "disableAutoscroll", void 0);
    __decorate([
        Prop(String),
        __metadata("design:type", String)
    ], FormFieldComponent.prototype, "suggestion", void 0);
    __decorate([
        Prop(String),
        __metadata("design:type", String)
    ], FormFieldComponent.prototype, "suggestionMessage", void 0);
    __decorate([
        Prop(Boolean),
        __metadata("design:type", Boolean)
    ], FormFieldComponent.prototype, "hasSuggestion", void 0);
    __decorate([
        Watch('hasProblem'),
        __metadata("design:type", Function),
        __metadata("design:paramtypes", [Boolean]),
        __metadata("design:returntype", void 0)
    ], FormFieldComponent.prototype, "onHasProblemChanged", null);
    __decorate([
        MagicEmit('input'),
        __metadata("design:type", Function),
        __metadata("design:paramtypes", [CustomEvent]),
        __metadata("design:returntype", void 0)
    ], FormFieldComponent.prototype, "onInput", null);
    __decorate([
        MagicEmit('change'),
        __metadata("design:type", Function),
        __metadata("design:paramtypes", [CustomEvent]),
        __metadata("design:returntype", void 0)
    ], FormFieldComponent.prototype, "onChange", null);
    __decorate([
        Emit('apply-suggestion'),
        __metadata("design:type", Function),
        __metadata("design:paramtypes", []),
        __metadata("design:returntype", void 0)
    ], FormFieldComponent.prototype, "applySuggestion", null);
    FormFieldComponent = __decorate([
        Component({
            name: 'ui-form-field',
            inheritAttrs: false,
            components: {
                'ui-input': InputComponent,
            },
        })
    ], FormFieldComponent);
    return FormFieldComponent;
}(Mixins(MuggleMixin, MagicConfigMixin)));

export default FormFieldComponent;
